import React, { useState, Fragment, useEffect, useMemo } from 'react';
import _ from 'lodash';

import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getTutorials } from '../../../store/tutorials';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import { CoverDataItem, PDFImageProps, User } from '../../../utils/models';
import { DEFAULT_REPORT_TITLE, FORBIDDEN_VALUES, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getCurrentUser } from '../../../services/authService';
import { reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import MPIReport from '.';
import MPIReportForm from '../../forms/custom-mpi/mpiReportForm';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import VIPhotoRecordForm from '../../forms/visual-inspection/viPhotoRecordForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useProjectDetails from '../../../hooks/useProjectDetails';
import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomMPIReport: React.FC = () => {
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [mpiReport, setMPIReport] = useState(initialMPIData);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [certificates, setCertificates] = useState<ICertificate>();

	const { fviCoverData } = useAppSelector(getCoverData);
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			projectDetails,
			mpiReport,
			coverData,
			executiveSummary,
			photoRecord,
			drawing,
			reportTitle,
			certificates,
		};
	}, [projectDetails, certificates, mpiReport, coverData, executiveSummary, photoRecord, drawing, reportTitle]);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
			return 'Provide a valid work order number';
		}

		if (!reportDetails.mpiReport.equipmentCalibrationDueDate) {
			return 'Provide a valid equipment calibration due date!';
		}
		
		if (!reportDetails.mpiReport.blackInkBatchNumber) {
			return 'Provide a valid black ink batch number';
		}
		
		if (!reportDetails.mpiReport.blackInkExpiryDate) {
			return 'Provide a valid black ink expiry date';
		}
		
		if (!reportDetails.mpiReport.whiteContrastBatchNumber) {
			return 'Provide a valid white contrast batch number';
		}
		
		if (!reportDetails.mpiReport.whiteContrastExpiryDate) {
			return 'Provide a valid white contrast expiry date';
		}
		
		if (!reportDetails.mpiReport.modal) {
			return 'Provide a valid modal value';
		}

		if (!reportDetails.mpiReport.pieGuageUsed) {
			return 'Select whether a pie guage was used!'
		}
		
		if (!reportDetails.mpiReport.liftingForceCheck) {
			return 'Select whether there was a lifting force check!'
		}

		if (!reportDetails.mpiReport.equipmentSerialNumber) {
			return 'Provide a valid MPI Equipment Serial Number!';
		}

		if (reportDetails.mpiReport.data.length === 0) {
			return 'MPI data cannot be empty!';
		}

		if (reportDetails.mpiReport.modal) {
			const modalValue = _.isString(reportDetails.mpiReport.modal) ? reportDetails.mpiReport.modal : (reportDetails.mpiReport.modal as CoverDataItem).value;
			if (FORBIDDEN_VALUES.includes(modalValue.toUpperCase()))
				return 'Provide a valid modal value';
		}

		if (!reportDetails.mpiReport.dateOfInspection) {
			return 'Provide a valid date of inspection!';
		}
		
		if (!reportDetails.mpiReport.serialNumber) {
			return 'Provide a valid serial number!';
		}

		if (reportDetails.mpiReport.serialNumber && FORBIDDEN_VALUES.includes(reportDetails.mpiReport.serialNumber.toUpperCase())) {
			return 'Provide a valid serial number!';
		}
		
		if (reportDetails.executiveSummary.length === 0)
			return 'Enter a valid summary!';

		if (reportDetails.photoRecord.length === 0)
			return 'Upload at least one photo!';

		if (reportDetails.drawing.length === 0)
			return 'General drawing not uploaded!';

		return '';
	}, [reportDetails]);

	const mappedPhotoNumbers = useMemo(() => {
		return photoRecord.map((photo) => ({
			label: photo.caption,
			value: photo.refNo,
		}));
	}, [photoRecord]);

	const togglePreviewReport = (): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	};

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	useEffect(() => {
		function initiateCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			const newCoverData = cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData;

			setCoverData(newCoverData);
			setDrawing(cachedReport.drawing);
			setMPIReport(cachedReport.mpiReport);
			setReportTitle(cachedReport.reportTitle);
			
			const summary = cachedReport.mpiReport.summary || cachedReport.executiveSummary || '';
			setExecutiveSummary(summary);
			
			setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
		}

		initiateCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.MPI));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<MPIReport {...reportDetails} />
			</Modal>

			<FVICoverData
				coverDataId={config.coverDataTemplates.exxonMobilMPT}
				isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilMPI && role.approveReports}
				subtitle="Update the default data for MPT Cover Data. This is the default data used on all MPT Reports"
			/>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title="Report Incomplete" body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

			<CustomCertificateForm calibrationRequired certificates={certificates} onChangeCertificate={setCertificates} />

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="Magnetic Particle Test Report" isValid={mpiReport.data.length > 0 && mpiReport.serialNumber.length > 0}>
				<MPIReportForm onAdd={setMPIReport} initialMPTData={mpiReport} photoNumbers={mappedPhotoNumbers} />
			</Zippy>

			<Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm
					initialGeneralPhotos={photoRecord}
					onProceed={setPhotoRecord}
				/>
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving..." />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialMPIData = {
	bgContrastMediumUsed: '',
	surfaceCondition: '',
	surfaceTemperature: '',
	magneticMediumUsed: '',
	equipmentCalibrationDueDate: new Date(),
	calibrationDueDate: new Date(),
	dateOfInspection: new Date(),
	blackInkBatchNumber: '',
	blackInkExpiryDate: new Date(),
	whiteContrastBatchNumber: '',
	whiteContrastExpiryDate: new Date(),
	modal: '',
	pieGuageUsed: '',
	liftingForceCheck: '',
	equipmentSerialNumber: '',
	currentUsed: '',
	serialNumber: '',
	brandName: '',
	material: '',
	method: '',
	stageOfTest: '',
	equipment: '',
	light: '',
	technique: '',
	summary: '',
	data: [],
};

export default CustomMPIReport;